import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { getAllStudents, approveStudent, rejectStudent, restrictStudent, unrestrictStudent } from '../../../services/adminService';
import { useAuth } from '../../../contexts/AuthContext';
import { useToast } from '../../../contexts/ToastContext';
import { approvalEmailService } from '../../../services/approvalEmailService';

const StudentApprovalPanel = () => {
  const { user } = useAuth();
  const toast = useToast();
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingId, setProcessingId] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchStudents();
  }, []);

  const fetchStudents = async () => {
    try {
      setLoading(true);
      const data = await getAllStudents();
      setStudents(data);
    } catch (error) {
      toast?.error(error?.message || 'Failed to load students');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (studentId) => {
    try {
      setProcessingId(studentId);
      await approveStudent(studentId, user?.id);
      
      // Send approval notification email
      const student = students?.find(s => s?.id === studentId);
      if (student?.email) {
        approvalEmailService?.sendStudentApprovalEmail({
          studentName: student?.name,
          studentEmail: student?.email
        });
      }
      
      toast?.success('Student approved successfully');
      await fetchStudents();
    } catch (error) {
      toast?.error(error?.message || 'Failed to approve student');
    } finally {
      setProcessingId(null);
    }
  };

  const handleReject = async (studentId) => {
    try {
      setProcessingId(studentId);
      await rejectStudent(studentId, user?.id);
      toast?.success('Student rejected');
      await fetchStudents();
    } catch (error) {
      toast?.error(error?.message || 'Failed to reject student');
    } finally {
      setProcessingId(null);
    }
  };

  const handleRestrict = async (studentId) => {
    try {
      setProcessingId(studentId);
      await restrictStudent(studentId, user?.id);
      toast?.success('Student access restricted');
      await fetchStudents();
    } catch (error) {
      toast?.error(error?.message || 'Failed to restrict student access');
    } finally {
      setProcessingId(null);
    }
  };

  const handleUnrestrict = async (studentId) => {
    try {
      setProcessingId(studentId);
      await unrestrictStudent(studentId);
      toast?.success('Student access restored');
      await fetchStudents();
    } catch (error) {
      toast?.error(error?.message || 'Failed to restore student access');
    } finally {
      setProcessingId(null);
    }
  };

  const filteredStudents = students?.filter(student => {
    let matchesStatus = true;
    if (filterStatus === 'pending') matchesStatus = student?.approvalStatus === 'pending';
    else if (filterStatus === 'approved') matchesStatus = student?.approvalStatus === 'approved' && !student?.isRestricted;
    else if (filterStatus === 'restricted') matchesStatus = student?.isRestricted === true;
    else if (filterStatus === 'rejected') matchesStatus = student?.approvalStatus === 'rejected';

    const matchesSearch = !searchQuery || 
      student?.name?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
      student?.email?.toLowerCase()?.includes(searchQuery?.toLowerCase());
    return matchesStatus && matchesSearch;
  });

  const getStatusBadge = (student) => {
    if (student?.isRestricted) {
      return (
        <span className="px-3 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
          Restricted
        </span>
      );
    }
    const statusConfig = {
      pending: { color: 'bg-yellow-100 text-yellow-800', label: 'Pending' },
      approved: { color: 'bg-green-100 text-green-800', label: 'Active' },
      rejected: { color: 'bg-red-100 text-red-800', label: 'Rejected' }
    };
    const config = statusConfig?.[student?.approvalStatus] || statusConfig?.pending;
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-medium ${config?.color}`}>
        {config?.label}
      </span>
    );
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      {/* Header */}
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Student Management</h2>
            <p className="text-sm text-gray-600 mt-1">
              Manage student registrations and access control
            </p>
          </div>
          <Button
            onClick={fetchStudents}
            variant="outline"
            size="sm"
            disabled={loading}
          >
            <Icon name="RefreshCw" className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* Info Banner */}
        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-start gap-2">
          <Icon name="Info" className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
          <p className="text-xs text-blue-700">
            Students are <strong>auto-approved</strong> after OTP verification. Use <strong>Restrict Access</strong> to withdraw a student's access. Restricted students will see a message to contact admin when they try to log in.
          </p>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Icon name="Search" className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search by name or email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e?.target?.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
          <div className="flex gap-2 flex-wrap">
            {['all', 'approved', 'restricted', 'pending', 'rejected']?.map((status) => (
              <button
                key={status}
                onClick={() => setFilterStatus(status)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  filterStatus === status
                    ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {status?.charAt(0)?.toUpperCase() + status?.slice(1)}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Students List */}
      <div className="overflow-x-auto">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Icon name="Loader" className="w-8 h-8 text-blue-600 animate-spin" />
          </div>
        ) : filteredStudents?.length === 0 ? (
          <div className="text-center py-12">
            <Icon name="Users" className="w-12 h-12 text-gray-400 mx-auto mb-3" />
            <p className="text-gray-600">No students found</p>
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Student
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Contact
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Course Interest
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Location
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Registered
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredStudents?.map((student) => (
                <tr key={student?.id} className={`hover:bg-gray-50 transition-colors ${student?.isRestricted ? 'bg-orange-50/30' : ''}`}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className={`flex-shrink-0 h-10 w-10 rounded-full flex items-center justify-center ${student?.isRestricted ? 'bg-orange-100' : 'bg-blue-100'}`}>
                        <span className={`font-semibold text-sm ${student?.isRestricted ? 'text-orange-600' : 'text-blue-600'}`}>
                          {student?.name?.charAt(0)?.toUpperCase()}
                        </span>
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">{student?.name}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{student?.email}</div>
                    <div className="text-sm text-gray-500">{student?.phone}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{student?.courseInterest || 'N/A'}</div>
                    <div className="text-sm text-gray-500">{student?.studyMode || 'N/A'}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{student?.city || 'N/A'}</div>
                    <div className="text-sm text-gray-500">{student?.state || 'N/A'}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {getStatusBadge(student)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {student?.registeredDate}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end gap-2">
                      {student?.approvalStatus === 'pending' && (
                        <>
                          <Button
                            onClick={() => handleApprove(student?.id)}
                            size="sm"
                            variant="success"
                            disabled={processingId === student?.id}
                          >
                            {processingId === student?.id ? (
                              <Icon name="Loader" className="w-4 h-4 animate-spin" />
                            ) : (
                              <>
                                <Icon name="Check" className="w-4 h-4 mr-1" />
                                Approve
                              </>
                            )}
                          </Button>
                          <Button
                            onClick={() => handleReject(student?.id)}
                            size="sm"
                            variant="danger"
                            disabled={processingId === student?.id}
                          >
                            <Icon name="X" className="w-4 h-4 mr-1" />
                            Reject
                          </Button>
                        </>
                      )}
                      {student?.approvalStatus === 'approved' && !student?.isRestricted && (
                        <Button
                          onClick={() => handleRestrict(student?.id)}
                          size="sm"
                          variant="outline"
                          disabled={processingId === student?.id}
                          className="text-orange-600 border-orange-300 hover:bg-orange-50"
                        >
                          {processingId === student?.id ? (
                            <Icon name="Loader" className="w-4 h-4 animate-spin" />
                          ) : (
                            <>
                              <Icon name="ShieldOff" className="w-4 h-4 mr-1" />
                              Restrict
                            </>
                          )}
                        </Button>
                      )}
                      {student?.isRestricted && (
                        <Button
                          onClick={() => handleUnrestrict(student?.id)}
                          size="sm"
                          variant="outline"
                          disabled={processingId === student?.id}
                          className="text-green-600 border-green-300 hover:bg-green-50"
                        >
                          {processingId === student?.id ? (
                            <Icon name="Loader" className="w-4 h-4 animate-spin" />
                          ) : (
                            <>
                              <Icon name="ShieldCheck" className="w-4 h-4 mr-1" />
                              Restore Access
                            </>
                          )}
                        </Button>
                      )}
                      {student?.approvalStatus === 'rejected' && (
                        <span className="text-red-600 font-medium text-sm">Rejected</span>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default StudentApprovalPanel;